import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  closeCreatorEventSales,
  creatorEventExportUrl,
  getCreatorEvent,
  listCreatorEventOrders,
  listCreatorEventRecentCheckins,
  openCreatorEventSales,
  cancelCreatorEvent,
  redeemEventTicketBySerial,
  redeemEventTicketFromQr,
  verifyEventTicketBySerial,
  verifyTicketQrPayload,
  type CreatorEventRow,
  type EventCheckinRow,
  type TicketVerifyResponse,
} from '../../../api/creatorEvents'
import { SettingsButton, SettingsCard, SettingsTitle } from '../../../components/settings/SettingsUI'
import ConfirmModal from './ConfirmModal'

type TabKey = 'orders' | 'payouts' | 'settings' | 'checkin'
type OrderStatusFilter = 'all' | 'paid' | 'failed' | 'refunded'

type OrderRow = {
  id: number
  buyer_id: number
  buyer_handle: string
  buyer_username: string
  qty: number
  price_pixe_each: number
  total_pixe: number
  fee_pixe: number
  net_pixe: number
  redeemed_qty?: number
  status: string
  created_at: string
  updated_at: string
}

function fmtDate(iso: string | null | undefined) {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
}

export function derivePaidTotals(orders: OrderRow[]) {
  return orders
    .filter((o) => String(o.status).toLowerCase() === 'paid')
    .reduce(
      (acc, o) => ({
        paid_qty: acc.paid_qty + Number(o.qty || 0),
        paid_gross_pixe: acc.paid_gross_pixe + Number(o.total_pixe || 0),
        paid_fee_pixe: acc.paid_fee_pixe + Number(o.fee_pixe || 0),
        paid_net_pixe: acc.paid_net_pixe + Number(o.net_pixe || 0),
        paid_redeemed_qty: acc.paid_redeemed_qty + Number(o.redeemed_qty || 0),
      }),
      { paid_qty: 0, paid_gross_pixe: 0, paid_fee_pixe: 0, paid_net_pixe: 0, paid_redeemed_qty: 0 }
    )
}

export default function PixeEventManage() {
  const params = useParams()
  const eventId = Number(params.postId)
  const [tab, setTab] = useState<TabKey>('orders')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [eventRow, setEventRow] = useState<CreatorEventRow | null>(null)
  const [orders, setOrders] = useState<OrderRow[]>([])
  const [orderTotals, setOrderTotals] = useState({ paid_qty: 0, paid_gross_pixe: 0, paid_fee_pixe: 0, paid_net_pixe: 0, paid_redeemed_qty: 0 })
  const [q, setQ] = useState('')
  const [statusFilter, setStatusFilter] = useState<OrderStatusFilter>('all')
  const [confirm, setConfirm] = useState<'cancel' | 'closeSales' | null>(null)
  const [working, setWorking] = useState(false)
  const [checkins, setCheckins] = useState<EventCheckinRow[]>([])
  const [checkInMode, setCheckInMode] = useState<'serial' | 'qr'>('serial')
  const [serialInput, setSerialInput] = useState('')
  const [qrInput, setQrInput] = useState('')
  const [verifyResult, setVerifyResult] = useState<TicketVerifyResponse | null>(null)
  const [verifyBusy, setVerifyBusy] = useState(false)
  const [redeemBusy, setRedeemBusy] = useState(false)

  async function load() {
    if (!Number.isFinite(eventId) || eventId <= 0) return
    setLoading(true)
    setError(null)
    try {
      const [eventData, ordersData, checkinsData] = await Promise.all([
        getCreatorEvent(eventId),
        listCreatorEventOrders(eventId, { q, status: statusFilter === 'all' ? undefined : statusFilter }),
        listCreatorEventRecentCheckins(eventId),
      ])
      setEventRow(eventData)
      const nextOrders = (ordersData.results || []) as OrderRow[]
      setOrders(nextOrders)
      setOrderTotals(ordersData.aggregates || derivePaidTotals(nextOrders))
      setCheckins(checkinsData.results || [])
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load event')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [eventId])

  useEffect(() => {
    const t = window.setTimeout(() => void load(), 220)
    return () => window.clearTimeout(t)
  }, [q, statusFilter])

  const remaining = useMemo(() => {
    if (!eventRow) return 0
    if (eventRow.tickets_remaining != null) return eventRow.tickets_remaining
    const cap = eventRow.event_capacity ?? 0
    return Math.max(0, cap - (eventRow.tickets_sold || 0))
  }, [eventRow])

  async function onOpenSales() {
    try {
      await openCreatorEventSales(eventId)
      await load()
    } catch (e: any) {
      window.alert(e?.response?.data?.detail || e?.message || 'Update failed')
    }
  }

  async function runConfirm() {
    if (!confirm) return
    setWorking(true)
    try {
      if (confirm === 'cancel') {
        await cancelCreatorEvent(eventId)
      } else {
        await closeCreatorEventSales(eventId)
      }
      setConfirm(null)
      await load()
    } catch (e: any) {
      window.alert(e?.response?.data?.detail || e?.message || 'Update failed')
    } finally {
      setWorking(false)
    }
  }

  async function onVerifyTicket() {
    if (!eventId || verifyBusy) return
    setVerifyBusy(true)
    try {
      if (checkInMode === 'serial') {
        const serial = serialInput.trim()
        if (!serial) {
          window.alert('Enter a ticket serial.')
          return
        }
        const data = await verifyEventTicketBySerial(eventId, serial)
        setVerifyResult(data)
        return
      }
      const raw = qrInput.trim()
      if (!raw) {
        window.alert('Paste QR payload JSON.')
        return
      }
      let payload: Record<string, any>
      try {
        payload = JSON.parse(raw)
      } catch {
        window.alert('Invalid JSON payload.')
        return
      }
      const data = await verifyTicketQrPayload(payload)
      setVerifyResult(data)
    } catch (e: any) {
      setVerifyResult({
        valid: false,
        detail: e?.response?.data?.detail || e?.message || 'Verification failed',
      })
    } finally {
      setVerifyBusy(false)
    }
  }

  async function onRedeemTicket() {
    if (!eventId || redeemBusy) return
    setRedeemBusy(true)
    try {
      if (checkInMode === 'serial') {
        const serial = serialInput.trim()
        if (!serial) {
          window.alert('Enter a ticket serial.')
          return
        }
        const data = await redeemEventTicketBySerial(eventId, serial, { source: 'manual' })
        setVerifyResult({
          valid: true,
          is_redeemed: true,
          redeemed_at: data?.redeemed_at,
          redeemed_by: data?.redeemed_by,
          ticket_status: 'redeemed',
          detail: data?.already_redeemed ? 'Already redeemed.' : 'Checked in successfully.',
        })
      } else {
        const raw = qrInput.trim()
        if (!raw) {
          window.alert('Paste QR payload JSON.')
          return
        }
        let payload: Record<string, any>
        try {
          payload = JSON.parse(raw)
        } catch {
          window.alert('Invalid JSON payload.')
          return
        }
        const data = await redeemEventTicketFromQr(eventId, payload, { source: 'qr' })
        setVerifyResult({
          valid: true,
          is_redeemed: true,
          redeemed_at: data?.redeemed_at,
          redeemed_by: data?.redeemed_by,
          ticket_status: 'redeemed',
          detail: data?.already_redeemed ? 'Already redeemed.' : 'Checked in successfully.',
        })
      }
      await load()
    } catch (e: any) {
      setVerifyResult({
        valid: false,
        detail: e?.response?.data?.detail || e?.message || 'Redeem failed',
      })
    } finally {
      setRedeemBusy(false)
    }
  }

  return (
    <div className="space-y-4">
      <SettingsTitle
        title="Manage Event"
        subtitle="Orders, payouts, and settings for this event."
        right={<Link to="/settings/creator-tools/events" className="text-sm text-white/70 hover:text-white">Back to Pixe Events</Link>}
      />

      {loading ? <div className="text-sm text-white/70">Loading…</div> : null}
      {error ? <div className="text-sm text-red-300">{error}</div> : null}

      {eventRow ? (
        <>
          <SettingsCard title={eventRow.title} subtitle={[eventRow.location_label, eventRow.event_venue_name].filter(Boolean).join(' • ') || 'Event details'}>
            <div className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-6">
              <div><div className="text-white/60">Start</div><div>{fmtDate(eventRow.event_starts_at)}</div></div>
              <div><div className="text-white/60">End</div><div>{fmtDate(eventRow.event_ends_at)}</div></div>
              <div><div className="text-white/60">Ticket price</div><div>{eventRow.event_ticket_price_pixe ?? 0} PIXE</div></div>
              <div><div className="text-white/60">Capacity</div><div>{eventRow.event_capacity ?? '—'}</div></div>
              <div><div className="text-white/60">Sold</div><div>{eventRow.tickets_sold}</div></div>
              <div><div className="text-white/60">Remaining</div><div>{remaining}</div></div>
            </div>
          </SettingsCard>

          <div className="flex flex-wrap gap-2">
            {(['orders', 'payouts', 'settings', 'checkin'] as TabKey[]).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setTab(k)}
                className={`rounded-full border px-3 py-1 text-sm ${tab === k ? 'bg-sky-600/20 border-sky-400/40 text-white' : 'bg-white/5 border-white/10 text-white/80 hover:bg-white/10'}`}
              >
                {k === 'orders' ? 'Orders / Attendees' : k === 'payouts' ? 'Payouts' : k === 'settings' ? 'Settings' : 'Check-In'}
              </button>
            ))}
          </div>

          {tab === 'orders' ? (
            <SettingsCard
              title="Orders / Attendees"
              subtitle="Search buyers, filter order status, and export CSV."
              right={
                <div className="flex gap-2">
                  <a href={creatorEventExportUrl(eventRow.id, 'orders', { q, status: statusFilter })} className="rounded border border-white/15 px-2 py-1 text-xs hover:bg-white/10">Export Orders CSV</a>
                  <a href={creatorEventExportUrl(eventRow.id, 'attendees', { q, status: statusFilter })} className="rounded border border-white/15 px-2 py-1 text-xs hover:bg-white/10">Export Attendees CSV</a>
                </div>
              }
            >
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search by handle"
                  className="w-full max-w-sm rounded-lg border border-white/15 bg-black/30 px-3 py-2 text-sm outline-none focus:border-white/30"
                />
                {(['all', 'paid', 'failed', 'refunded'] as OrderStatusFilter[]).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setStatusFilter(s)}
                    className={`rounded-full border px-2.5 py-1 text-xs ${statusFilter === s ? 'border-white/30 bg-white/15' : 'border-white/10 bg-white/5 hover:bg-white/10'}`}
                    disabled={s === 'refunded'}
                    title={s === 'refunded' ? 'No refunds in v1' : undefined}
                  >
                    {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
                  </button>
                ))}
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-white/65">
                      <th className="px-2 py-2">Order</th>
                      <th className="px-2 py-2">Buyer</th>
                      <th className="px-2 py-2">Qty</th>
                      <th className="px-2 py-2">Total</th>
                      <th className="px-2 py-2">Fee</th>
                      <th className="px-2 py-2">Net</th>
                      <th className="px-2 py-2">Redeemed</th>
                      <th className="px-2 py-2">Status</th>
                      <th className="px-2 py-2">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((row) => (
                      <tr key={row.id} className="border-t border-white/10">
                        <td className="px-2 py-1.5">#{row.id}</td>
                        <td className="px-2 py-1.5">
                          <div className="inline-flex items-center gap-1.5">
                            <Link className="text-sky-200 hover:underline" to={`/people?q=${encodeURIComponent(row.buyer_handle || row.buyer_username)}`}>
                              @{row.buyer_handle || row.buyer_username}
                            </Link>
                            <button
                              type="button"
                              onClick={() => void navigator.clipboard?.writeText(`@${row.buyer_handle || row.buyer_username}`)}
                              className="rounded border border-white/15 px-1.5 py-0.5 text-[10px] hover:bg-white/10"
                            >
                              Copy
                            </button>
                          </div>
                        </td>
                        <td className="px-2 py-1.5">{row.qty}</td>
                        <td className="px-2 py-1.5">{row.total_pixe}</td>
                        <td className="px-2 py-1.5">{row.fee_pixe}</td>
                        <td className="px-2 py-1.5">{row.net_pixe}</td>
                        <td className="px-2 py-1.5">{Number(row.redeemed_qty || 0)}/{row.qty}</td>
                        <td className="px-2 py-1.5">{row.status}</td>
                        <td className="px-2 py-1.5">{fmtDate(row.created_at)}</td>
                      </tr>
                    ))}
                    {!orders.length ? (
                      <tr>
                        <td className="px-2 py-3 text-white/60" colSpan={9}>No orders yet.</td>
                      </tr>
                    ) : null}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-white/20 bg-white/[0.03] text-xs text-white/80">
                      <td className="px-2 py-2" colSpan={2}>Paid totals</td>
                      <td className="px-2 py-2 font-semibold">{orderTotals.paid_qty}</td>
                      <td className="px-2 py-2 font-semibold">{orderTotals.paid_gross_pixe}</td>
                      <td className="px-2 py-2 font-semibold">{orderTotals.paid_fee_pixe}</td>
                      <td className="px-2 py-2 font-semibold">{orderTotals.paid_net_pixe}</td>
                      <td className="px-2 py-2 font-semibold">{orderTotals.paid_redeemed_qty ?? 0}</td>
                      <td className="px-2 py-2" colSpan={2}></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </SettingsCard>
          ) : null}

          {tab === 'payouts' ? (
            <SettingsCard title="Payouts" subtitle="Gross, platform fee, and net breakdown.">
              <div className="grid gap-3 text-sm sm:grid-cols-3">
                <div className="rounded border border-white/10 p-3"><div className="text-white/60">Gross</div><div className="text-xl font-bold">{eventRow.gross_pixe} PIXE</div></div>
                <div className="rounded border border-white/10 p-3"><div className="text-white/60">Platform fee</div><div className="text-xl font-bold">{eventRow.fee_pixe} PIXE</div></div>
                <div className="rounded border border-white/10 p-3"><div className="text-white/60">Net to creator</div><div className="text-xl font-bold">{eventRow.net_pixe} PIXE</div></div>
              </div>
            </SettingsCard>
          ) : null}

          {tab === 'settings' ? (
            <SettingsCard title="Settings" subtitle="Close/open sales and cancel event.">
              <div className="flex flex-wrap gap-2">
                {eventRow.event_sales_closed ? (
                  <SettingsButton variant="secondary" onClick={() => void onOpenSales()}>Open sales</SettingsButton>
                ) : (
                  <SettingsButton variant="secondary" onClick={() => setConfirm('closeSales')}>Close sales</SettingsButton>
                )}
                <SettingsButton variant="danger" onClick={() => setConfirm('cancel')} disabled={eventRow.event_is_cancelled}>
                  {eventRow.event_is_cancelled ? 'Cancelled' : 'Cancel event'}
                </SettingsButton>
              </div>
              <div className="mt-2 text-xs text-white/60">Refund flow is not included in v1.</div>
            </SettingsCard>
          ) : null}

          {tab === 'checkin' ? (
            <SettingsCard title="Check-In" subtitle="Verify and redeem tickets at the door.">
              <div className="grid gap-4 lg:grid-cols-2">
                <div className="space-y-3 rounded-xl border border-white/10 bg-black/20 p-3">
                  <div className="inline-flex rounded-lg border border-white/10 bg-black/30 p-1">
                    <button
                      type="button"
                      data-testid="checkin-mode-serial"
                      onClick={() => setCheckInMode('serial')}
                      className={`rounded-md px-2 py-1 text-xs ${checkInMode === 'serial' ? 'bg-white/15 text-white' : 'text-white/70 hover:bg-white/10'}`}
                    >
                      Ticket serial
                    </button>
                    <button
                      type="button"
                      data-testid="checkin-mode-qr"
                      onClick={() => setCheckInMode('qr')}
                      className={`rounded-md px-2 py-1 text-xs ${checkInMode === 'qr' ? 'bg-white/15 text-white' : 'text-white/70 hover:bg-white/10'}`}
                    >
                      QR payload
                    </button>
                  </div>
                  {checkInMode === 'serial' ? (
                    <input
                      data-testid="checkin-serial-input"
                      value={serialInput}
                      onChange={(e) => setSerialInput(e.target.value)}
                      placeholder="Ticket serial UUID"
                      className="w-full rounded-lg border border-white/15 bg-black/30 px-3 py-2 text-sm outline-none focus:border-white/30"
                    />
                  ) : (
                    <textarea
                      data-testid="checkin-qr-input"
                      value={qrInput}
                      onChange={(e) => setQrInput(e.target.value)}
                      placeholder='Paste QR payload JSON {"kind":"ticket",...}'
                      rows={7}
                      className="w-full rounded-lg border border-white/15 bg-black/30 px-3 py-2 text-xs outline-none focus:border-white/30"
                    />
                  )}
                  <div className="flex gap-2">
                    <SettingsButton
                      data-testid="checkin-verify-action"
                      variant="secondary"
                      onClick={() => void onVerifyTicket()}
                      disabled={verifyBusy}
                    >
                      {verifyBusy ? 'Verifying…' : 'Verify'}
                    </SettingsButton>
                    <SettingsButton data-testid="checkin-redeem-action" onClick={() => void onRedeemTicket()} disabled={redeemBusy}>
                      {redeemBusy ? 'Redeeming…' : 'Redeem / Check in'}
                    </SettingsButton>
                  </div>
                  {verifyResult ? (
                    <div
                      className={`rounded-lg border p-2 text-sm ${
                        verifyResult.valid
                          ? verifyResult.is_redeemed
                            ? 'border-rose-400/30 bg-rose-500/10 text-rose-100'
                            : 'border-emerald-400/30 bg-emerald-500/10 text-emerald-100'
                          : 'border-rose-400/30 bg-rose-500/10 text-rose-100'
                      }`}
                    >
                      {!verifyResult.valid
                        ? `Invalid ticket: ${verifyResult.detail || 'verification failed'}`
                        : verifyResult.is_redeemed
                        ? `Already redeemed${verifyResult.redeemed_at ? ` at ${fmtDate(verifyResult.redeemed_at)}` : ''}.`
                        : 'Valid ticket'}
                    </div>
                  ) : null}
                </div>
                <div className="rounded-xl border border-white/10 bg-black/20 p-3">
                  <div className="mb-2 text-sm font-semibold">Recent check-ins</div>
                  <div className="space-y-2">
                    {checkins.length ? (
                      checkins.map((row) => (
                        <div key={`${row.ticket_serial}-${row.redeemed_at}`} className="rounded-lg border border-white/10 bg-black/20 p-2 text-xs">
                          <div className="font-medium text-white/90">{row.buyer_handle || row.buyer_username}</div>
                          <div className="text-white/70">{row.ticket_serial}</div>
                          <div className="text-white/60">{fmtDate(row.redeemed_at)}</div>
                        </div>
                      ))
                    ) : (
                      <div className="text-xs text-white/60">No check-ins yet.</div>
                    )}
                  </div>
                </div>
              </div>
            </SettingsCard>
          ) : null}
        </>
      ) : null}

      <ConfirmModal
        open={Boolean(confirm)}
        title={confirm === 'cancel' ? 'Cancel event?' : 'Close ticket sales?'}
        body={
          confirm === 'cancel'
            ? 'This stops ticket sales immediately. Refunds are not automatic yet.'
            : 'You can reopen ticket sales later from settings.'
        }
        confirmLabel={confirm === 'cancel' ? 'Cancel event' : 'Close sales'}
        cancelLabel={confirm === 'cancel' ? 'Keep event' : 'Keep open'}
        danger={confirm === 'cancel'}
        busy={working}
        onCancel={() => setConfirm(null)}
        onConfirm={() => void runConfirm()}
      />
    </div>
  )
}
